import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { database } from '../firebaseConfig';
import { ref, get, update } from 'firebase/database';

function PayPage() {
  const { id } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userRef = ref(database, `users/${id}`);
        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          const data = { id, ...snapshot.val() };
          setUserData(data);
          // Check if payment is already completed
          if (data.paymentStatus === true) {
            setPaymentSuccess(true);
          }
        } else {
          setError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Error loading data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id]);

  const handlePayment = () => {
    const amountPerMother = 1000;
    const totalAmount = userData.mothersAdapted * amountPerMother;

    const options = {
      key: 'rzp_live_Rer128JDWmcK0E', // Replace with your Razorpay key
      amount: totalAmount * 100, // Amount in paise
      currency: 'INR',
      name: 'Savemom',
      description: `Adopt ${userData.mothersAdapted} Mother(s)`,
      image: 'https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e',
      handler: async function (response) {
        setPaymentProcessing(true);
        try {
          // Store payment data in Firebase
          const userRef = ref(database, `users/${id}`);
          await update(userRef, {
            paymentStatus: true,
            paymentData: {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id || null,
              razorpay_signature: response.razorpay_signature || null,
              amount: totalAmount,
              currency: 'INR',
              paidAt: Date.now(),
              mothersAdopted: userData.mothersAdapted
            }
          });
          setPaymentSuccess(true);
        } catch (err) {
          console.error('Error saving payment data:', err);
          alert('Payment successful but failed to update records. Please contact support.');
        } finally {
          setPaymentProcessing(false);
        }
      },
      prefill: {
        name: userData.name,
        contact: userData.contact,
      },
      notes: {
        userId: id,
        mothersAdopted: userData.mothersAdapted
      },
      theme: {
        color: '#059669'
      },
      modal: {
        ondismiss: function() {
          setPaymentProcessing(false);
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', function (response) {
      console.error('Payment failed:', response.error);
      alert(`Payment failed: ${response.error.description}`);
    });
    razorpay.open();
  };

  if (loading) {
    return (
      <div className="App pay-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App pay-page">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h2>{error}</h2>
          <Link to="/" className="back-button">Go to Home</Link>
        </div>
      </div>
    );
  }

  const amountPerMother = 1000;
  const totalAmount = userData.mothersAdapted * amountPerMother;

  return (
    <div className="App pay-page">
      <Helmet>
        <title>Complete Your Payment - Adopt a Mother | Savemom</title>
        <meta name="description" content={`Complete your payment for adopting ${userData.mothersAdapted} mother(s). Thank you for supporting expecting mothers through Savemom's Adopt a Mother Initiative.`} />
        <meta property="og:title" content="Complete Your Payment - Adopt a Mother | Savemom" />
        <meta property="og:description" content="Complete your payment to finalize your adoption of mothers. Your contribution helps provide essential healthcare to expecting mothers." />
        <meta property="og:image" content="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Complete Your Payment - Adopt a Mother | Savemom" />
        <meta name="twitter:description" content="Complete your payment to finalize your adoption of mothers." />
        <meta name="twitter:image" content="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
        <link rel="icon" href="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
      </Helmet>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="logo-section">
            <Link to="/" className="logo-link">
              <div className="logo-icon">💚</div>
              <div className="logo-text">
                <h1>Savemom</h1>
                <span>Adopt a Mother Initiative</span>
              </div>
            </Link>
          </div>
        </div>
      </header>

      {/* Payment Details */}
      <main className="pay-content">
        <div className="pay-card">
          <div className="pay-header">
            <h2>💚 Payment Details</h2>
            <p>Thank you for your generosity!</p>
          </div>

          <div className="donor-info">
            <h3>Donor Information</h3>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{userData.name}</span>
            </div>
            <div className="info-row">
              <span className="label">Contact:</span>
              <span className="value">{userData.contact}</span>
            </div>
            {userData.address && (
              <div className="info-row">
                <span className="label">Address:</span>
                <span className="value">{userData.address}</span>
              </div>
            )}
          </div>

          <div className="adoption-details">
            <h3>Adoption Details</h3>
            <div className="info-row highlight">
              <span className="label">Mothers Adopted:</span>
              <span className="value mothers-count">{userData.mothersAdapted}</span>
            </div>
            <div className="info-row">
              <span className="label">Amount per Mother:</span>
              <span className="value">₹{amountPerMother.toLocaleString()}</span>
            </div>
            <div className="info-row total">
              <span className="label">Total Amount:</span>
              <span className="value total-amount">₹{totalAmount.toLocaleString()}</span>
            </div>
          </div>

          <div className="payment-section">
            <h3>{paymentSuccess ? '✅ Payment Completed' : 'Complete Your Payment'}</h3>
            {paymentSuccess ? (
              <div className="payment-success">
                <p className="success-message">
                  Thank you! Your payment has been successfully processed.
                </p>
                <p className="success-details">
                  You have adopted {userData.mothersAdapted} mother(s) and made a difference in their lives.
                </p>
              </div>
            ) : (
              <>
                <p className="payment-note">
                  Please complete your payment to finalize your adoption of {userData.mothersAdapted} mother(s).
                </p>
                
                <div className="payment-options">
                  <button 
                    className="pay-button primary" 
                    onClick={handlePayment}
                    disabled={paymentProcessing}
                  >
                    {paymentProcessing ? 'Processing...' : `Pay ₹${totalAmount.toLocaleString()} Now`}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="pay-footer">
            <p>Your contribution helps provide essential healthcare to expecting mothers.</p>
            <Link to="/" className="back-link">← Back to Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

export default PayPage;
